<template>
    <div id="order-tracking" class="order-tracking">
        <header class="header-operator">
            <span class="header__back" @click="redirectPage('operator/menu/orders')">
                <BackIcon />
            </span>

            <span class="header__title">
                {{ $t("OrderTracking.Title") }}
            </span>
        </header>

        <div class="order-tracking__content">
            <section class="order-tracking__filters-card">
                <div class="order-tracking__filters-row">
                    <div class="order-tracking__filter-group">
                        <span class="order-tracking__filter-label">
                            {{ $t("OrderTracking.FilterBy") }}
                        </span>
                        <div class="order-tracking__filter-buttons">
                            <button v-for="option in filterOptions" :key="option.value" type="button" :class="[
                                'order-tracking__filter-btn',
                                filterType === option.value ? 'active' : ''
                            ]" @click="setFilterType(option.value)">
                                {{ $t(option.labelKey) }}
                            </button>
                        </div>
                    </div>

                    <div class="order-tracking__input-group">
                        <label class="order-tracking__input-label">
                            {{ $t(activeFilterInputLabel) }}
                        </label>
                        <input v-model="filterText" type="text" class="order-tracking__input"
                            @input="onFilterTextInput" />
                    </div>

                    <div class="order-tracking__input-group order-tracking__date-group">
                        <label class="order-tracking__input-label">
                            {{ $t("OrderTracking.StartDate") }}
                        </label>
                        <input v-model="startDate" type="date" class="order-tracking__input"
                            @change="onStartDateChange" />
                    </div>

                    <button
                        type="button"
                        class="order-tracking__clear-filter-btn"
                        :disabled="!hasActiveFilter"
                        @click="clearFilters"
                    >
                        {{ $t("Operator.CleanFilter") }}
                    </button>
                </div>
            </section>

            <section class="order-tracking__legend-card">
                <span class="order-tracking__legend-title">
                    {{ $t("ResourceLoading.Legend") }}
                </span>
                <div class="order-tracking__legend-items">
                    <div v-for="item in legendItems" :key="item.key" class="order-tracking__legend-item">
                        <span class="order-tracking__legend-color" :style="{
                            backgroundColor: item.color,
                            color: item.textColor
                        }" />
                        <span class="order-tracking__legend-text">
                            {{ $t(item.labelKey) }}
                        </span>
                    </div>
                </div>
            </section>

            <section class="order-tracking__table-card">
                <div class="order-tracking__table-wrapper">
                    <table class="order-tracking__table">
                        <thead>
                            <tr>
                                <th>{{ $t("OrderTracking.Columns.Order") }}</th>
                                <th>{{ $t("OrderTracking.Columns.ProductCode") }}</th>
                                <th>{{ $t("OrderTracking.Columns.Description") }}</th>
                                <th class="text-right">
                                    {{ $t("OrderTracking.Columns.Quantity") }}
                                </th>
                                <th>{{ $t("OrderTracking.Columns.PlannedStartDate") }}</th>
                                <th>{{ $t("OrderTracking.Columns.PurchaseOrder") }}</th>
                                <th>{{ $t("OrderTracking.Columns.Customer") }}</th>
                                <th>{{ $t("OrderTracking.Columns.Progress") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="order in orders" :key="getOrderKey(order)" :class="{
                                'order-tracking__row--selected':
                                    selectedOrder && getOrderKey(selectedOrder) === getOrderKey(order)
                            }" :style="getRowStyle(order)" @click="selectOrder(order)">
                                <td>{{ getOrderField(order, "orderNumber") }}</td>
                                <td>{{ getOrderField(order, "materialCode") }}</td>
                                <td>{{ getOrderField(order, "description") }}</td>
                                <td class="text-right">
                                    {{ formatQuantity(getOrderField(order, "quantity")) }}
                                </td>
                                <td>{{ formatDate(getOrderField(order, "plannedStartDate")) }}</td>
                                <td>{{ getOrderField(order, "purchaseOrder") }}</td>
                                <td>{{ getOrderField(order, "customerName") }}</td>
                                <td class="order-tracking__progress-cell">
                                    <div class="order-tracking__progress">
                                        <div class="order-tracking__progress-track">
                                            <div class="order-tracking__progress-fill" :style="{
                                                width: `${getOrderProgress(order)}%`,
                                                backgroundColor: getProgressBarColor(
                                                    getOrderProgress(order)
                                                )
                                            }" />
                                        </div>
                                        <span class="order-tracking__progress-label">
                                            {{ formatProgressLabel(getOrderProgress(order)) }}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="!loading && orders.length === 0">
                                <td colspan="8" class="order-tracking__empty-cell">
                                    {{ $t("OrderTracking.NoOrdersFound") }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="totalOrders > pageSize" class="order-tracking__pagination">
                    <b-pagination v-model="page" :total-rows="totalOrders" :per-page="pageSize" align="right" size="sm"
                        class="my-0" @change="fetchOrders()" />
                </div>
            </section>

            <section class="order-tracking__table-card order-tracking__operations-card">
                <h2 class="order-tracking__section-title">
                    {{ operationsSectionTitle }}
                </h2>
                <div class="order-tracking__table-wrapper">
                    <table class="order-tracking__table">
                        <thead>
                            <tr>
                                <th>{{ $t("OrderTracking.Columns.Order") }}</th>
                                <th>{{ $t("OrderTracking.Columns.Operation") }}</th>
                                <th>{{ $t("OrderTracking.Columns.OperationDescription") }}</th>
                                <th>{{ $t("OrderTracking.Columns.Resource") }}</th>
                                <th class="text-right">
                                    {{ $t("OrderTracking.Columns.Quantity") }}
                                </th>
                                <th class="text-right">
                                    {{ $t("OrderTracking.Columns.ProducedQuantity") }}
                                </th>
                                <th>{{ $t("OrderTracking.Columns.CurrentStatus") }}</th>
                                <th class="text-right">
                                    {{ $t("OrderTracking.Columns.Fulfillment") }}
                                </th>
                            </tr>
                        </thead>
                        <tbody v-if="selectedOperations.length">
                            <tr v-for="(operation, index) in selectedOperations"
                                :key="`${getOperationKey(operation)}-${index}`" :style="getRowStyle(operation)">
                                <td>{{ getOperationField(operation, "orderNumber") }}</td>
                                <td>{{ getOperationField(operation, "operationNumber") }}</td>
                                <td>{{ getOperationField(operation, "operationDescription") }}</td>
                                <td>{{ getOperationField(operation, "resourceName") }}</td>
                                <td class="text-right">
                                    {{ formatQuantity(getOperationField(operation, "quantity")) }}
                                </td>
                                <td class="text-right">
                                    {{
                                        formatQuantity(
                                            getOperationField(operation, "producedQuantity")
                                        )
                                    }}
                                </td>
                                <td>{{ getOperationField(operation, "currentStatus") }}</td>
                                <td class="text-right">
                                    {{ formatPercentage(getOperationField(operation, "fulfillmentPercentage")) }}
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr>
                                <td colspan="8" class="order-tracking__empty-cell">
                                    {{ $t("OrderTracking.SelectOrderHint") }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { BPagination } from "bootstrap-vue";
import { mapMutations } from "vuex";

const FILTER_TYPES = {
    customer: "customer",
    order: "order",
    purchase: "purchase",
    product: "product"
};

export default {
    components: {
        BackIcon: () => import("@/assets/images/icons/back.svg"),
        BPagination
    },
    data() {
        return {
            currentSite: this.$cookies.get("userInfo").currentSite,
            filterType: FILTER_TYPES.customer,
            filterText: "",
            defaultStartDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
            startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
            orders: [],
            selectedOrder: null,
            loading: false,
            page: 1,
            pageSize: 10,
            totalOrders: 0,
            debounceTimer: null,
            refreshInterval: null,
            filterOptions: [
                { value: FILTER_TYPES.customer, labelKey: "OrderTracking.Filters.Customer" },
                { value: FILTER_TYPES.order, labelKey: "OrderTracking.Filters.OrderNumber" },
                { value: FILTER_TYPES.purchase, labelKey: "OrderTracking.Filters.PurchaseOrder" },
                { value: FILTER_TYPES.product, labelKey: "OrderTracking.Filters.Product" }
            ],
            legendItems: [
                {
                    key: "started",
                    color: "#C5E8C5",
                    textColor: "#4c4541",
                    labelKey: "OrderTracking.Legend.Started"
                },
                {
                    key: "running",
                    color: "#1B7A43",
                    textColor: "#ffffff",
                    labelKey: "OrderTracking.Legend.Running"
                },
                {
                    key: "partial",
                    color: "#FFF3A0",
                    textColor: "#4c4541",
                    labelKey: "OrderTracking.Legend.PartialFinished"
                },
                {
                    key: "total",
                    color: "#B3D9F2",
                    textColor: "#4c4541",
                    labelKey: "OrderTracking.Legend.TotalFinished"
                },
                {
                    key: "notStarted",
                    color: "#F3EFEC",
                    textColor: "#4c4541",
                    labelKey: "OrderTracking.Legend.NotStarted"
                }
            ]
        };
    },
    computed: {
        activeFilterInputLabel() {
            const labels = {
                [FILTER_TYPES.customer]: "OrderTracking.Inputs.CustomerCode",
                [FILTER_TYPES.order]: "OrderTracking.Inputs.OrderNumber",
                [FILTER_TYPES.purchase]: "OrderTracking.Inputs.PurchaseOrder",
                [FILTER_TYPES.product]: "OrderTracking.Inputs.ProductCode"
            };

            return labels[this.filterType];
        },
        hasActiveFilter() {
            return !!this.filterText.trim() || this.startDate !== this.defaultStartDate;
        },
        selectedOperations() {
            if (!this.selectedOrder) return [];

            return this.normalizeOperations(this.selectedOrder);
        },
        operationsSectionTitle() {
            if (!this.selectedOrder) {
                return this.$t("OrderTracking.ProductionSituations");
            }

            const orderNumber = this.getOrderField(this.selectedOrder, "orderNumber");

            return `${this.$t("OrderTracking.ProductionSituations")} — ${this.$t(
                "OrderTracking.OrderLabel"
            )} ${orderNumber}`;
        }
    },
    created() {
        this.fetchOrders();
        this.refreshInterval = setInterval(() => {
            this.fetchOrders({ silent: true });
        }, 30000);
    },
    beforeDestroy() {
        clearTimeout(this.debounceTimer);
        clearInterval(this.refreshInterval);
    },
    methods: {
        ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
        redirectPage(route) {
            const prefix =
                localStorage.getItem("prefix") || this.$router.currentRoute.params.prefix;

            this.$router.push({ path: `/${prefix}/${route}` });
        },
        setFilterType(type) {
            if (this.filterType === type) return;

            this.filterType = type;
            this.filterText = "";
            clearTimeout(this.debounceTimer);
        },
        onFilterTextInput() {
            clearTimeout(this.debounceTimer);

            if (!this.filterText.trim()) return;

            this.debounceTimer = setTimeout(() => {
                this.page = 1;
                this.fetchOrders();
            }, 3000);
        },
        onStartDateChange() {
            this.page = 1;
            this.fetchOrders();
        },
        clearFilters() {
            clearTimeout(this.debounceTimer);
            this.filterText = "";
            this.startDate = this.defaultStartDate;
            this.page = 1;
            this.fetchOrders();
        },
        buildRequestParams() {
            const params = {
                siteId: this.currentSite,
                startDate: this.startDate,
                page: this.page,
                pageSize: this.pageSize
            };

            const trimmedFilter = this.filterText.trim();

            if (trimmedFilter) {
                const paramMap = {
                    [FILTER_TYPES.customer]: "customerCode",
                    [FILTER_TYPES.order]: "orderNumber",
                    [FILTER_TYPES.purchase]: "purchaseOrder",
                    [FILTER_TYPES.product]: "materialCode"
                };

                params[paramMap[this.filterType]] = trimmedFilter;
            }

            return params;
        },
        fetchOrders({ silent = false } = {}) {
            if (!silent) {
                this.loading = true;
                this.UPDATE_FLAG_SHOW_OVERLAY(true);
            }

            this.$http2
                .get("/api/mysfc/operation/order-status", {
                    params: this.buildRequestParams()
                })
                .then((response) => {
                    const orders = this.extractOrders(response.data);
                    this.orders = orders;
                    this.totalOrders = this.extractTotalOrders(response.data, orders.length);
                    this.syncSelectedOrder(orders);
                })
                .catch((error) => {
                    console.error("Erro ao buscar status das ordens:", error);

                    if (!silent) {
                        this.orders = [];
                        this.totalOrders = 0;
                        this.selectedOrder = null;
                    }
                })
                .finally(() => {
                    if (!silent) {
                        this.loading = false;
                        this.UPDATE_FLAG_SHOW_OVERLAY(false);
                    }
                });
        },
        extractOrders(payload) {
            if (Array.isArray(payload)) return payload;
            if (Array.isArray(payload?.data)) return payload.data;
            if (Array.isArray(payload?.data?.orders)) return payload.data.orders;
            if (Array.isArray(payload?.orders)) return payload.orders;

            return [];
        },
        extractTotalOrders(payload, fallback = 0) {
            const total =
                payload?.data?.totalItems ??
                payload?.data?.totalCount ??
                payload?.data?.total ??
                payload?.totalItems ??
                payload?.totalCount ??
                payload?.total;

            if (typeof total === "number") return total;

            return fallback;
        },
        syncSelectedOrder(orders) {
            if (!this.selectedOrder) return;

            const selectedKey = this.getOrderKey(this.selectedOrder);
            const updatedOrder = orders.find((order) => this.getOrderKey(order) === selectedKey);

            this.selectedOrder = updatedOrder || null;
        },
        selectOrder(order) {
            this.selectedOrder = order;
        },
        normalizeOperations(order) {
            const operations =
                order?.operations ||
                order?.Operations ||
                order?.operationList ||
                order?.OperationList ||
                [];

            return Array.isArray(operations) ? operations : [];
        },
        getOrderKey(order) {
            return (
                order?.orderNumber ||
                order?.OrderNumber ||
                order?.order ||
                order?.Order ||
                order?.orderId ||
                order?.OrderId ||
                order?.id ||
                order?.Id
            );
        },
        getOperationKey(operation) {
            return (
                operation?.operationNumber ||
                operation?.OperationNumber ||
                operation?.operationCode ||
                operation?.OperationCode ||
                operation?.id ||
                operation?.Id
            );
        },
        getOrderField(order, field) {
            const fieldMap = {
                orderNumber: ["orderNumber", "OrderNumber", "order", "Order", "number", "Number"],
                materialCode: ["materialCode", "MaterialCode", "productCode", "ProductCode"],
                description: [
                    "description",
                    "Description",
                    "productDescription",
                    "ProductDescription",
                    "productName",
                    "ProductName"
                ],
                quantity: ["quantity", "Quantity", "plannedQuantity", "PlannedQuantity"],
                plannedStartDate: [
                    "plannedStartDate",
                    "PlannedStartDate",
                    "orderStart",
                    "OrderStart",
                    "startDate",
                    "StartDate"
                ],
                purchaseOrder: ["purchaseOrder", "PurchaseOrder", "customerOrder", "CustomerOrder"],
                customerName: ["customerName", "CustomerName", "customer", "Customer"],
                completedPercentage: [
                    "completedPercentage",
                    "CompletedPercentage",
                    "progress",
                    "Progress",
                    "fulfillmentPercentage",
                    "FulfillmentPercentage"
                ],
                statusColor: ["statusColor", "StatusColor", "color", "Color"]
            };

            return this.getFieldValue(order, fieldMap[field] || [field]);
        },
        getOperationField(operation, field) {
            const fieldMap = {
                orderNumber: ["orderNumber", "OrderNumber", "order", "Order"],
                operationNumber: [
                    "operationNumber",
                    "OperationNumber",
                    "operationCode",
                    "OperationCode",
                    "number",
                    "Number"
                ],
                operationDescription: [
                    "operationDescription",
                    "OperationDescription",
                    "description",
                    "Description",
                    "operationName",
                    "OperationName",
                    "name",
                    "Name"
                ],
                resourceName: [
                    "resourceName",
                    "ResourceName",
                    "resource",
                    "Resource",
                    "resources",
                    "Resources"
                ],
                quantity: ["quantity", "Quantity", "plannedQuantity", "PlannedQuantity"],
                producedQuantity: [
                    "producedQuantity",
                    "ProducedQuantity",
                    "quantityProduced",
                    "QuantityProduced"
                ],
                currentStatus: [
                    "currentStatus",
                    "CurrentStatus",
                    "statusName",
                    "StatusName",
                    "status",
                    "Status"
                ],
                fulfillmentPercentage: [
                    "fulfillmentPercentage",
                    "FulfillmentPercentage",
                    "completedPercentage",
                    "CompletedPercentage",
                    "attendancePercentage",
                    "AttendancePercentage",
                    "servicePercentage",
                    "ServicePercentage",
                    "totalCompleted",
                    "TotalCompleted"
                ],
                statusColor: ["statusColor", "StatusColor", "color", "Color"]
            };

            const value = this.getFieldValue(operation, fieldMap[field] || [field]);

            if (field === "resourceName" && Array.isArray(value)) {
                return value.map((item) => item?.name || item?.Name || item).join(", ");
            }

            return value;
        },
        getFieldValue(source, keys) {
            if (!source) return "";

            for (const key of keys) {
                const value = source[key];

                if (value !== undefined && value !== null && value !== "") {
                    return value;
                }
            }

            return "";
        },
        getRowStyle(item) {
            const isOrder = Array.isArray(item?.operations) || Array.isArray(item?.Operations);
            const backgroundColor = isOrder
                ? this.resolveOrderRowColor(item)
                : this.resolveOperationRowColor(item);

            if (!backgroundColor) return {};

            const legendItem = this.legendItems.find((legend) => legend.color === backgroundColor);

            return {
                backgroundColor,
                color: legendItem?.textColor || this.getContrastTextColor(backgroundColor)
            };
        },
        getLegendColor(key) {
            return this.legendItems.find((item) => item.key === key)?.color || null;
        },
        getOperationStatus(operation) {
            return String(this.getOperationField(operation, "currentStatus") || "").trim();
        },
        isFinishedStatus(status) {
            return status === "Operação Finalizada" || status === "Completed";
        },
        isNotStartedStatus(status) {
            return !status || status === "Não Iniciado" || status === "Not Started";
        },
        isRunningStatus(status) {
            return status === "Executando" || status === "Running";
        },
        resolveOperationRowColor(operation) {
            const explicitColor = this.getOperationField(operation, "statusColor");

            if (explicitColor) return explicitColor;

            const status = this.getOperationStatus(operation);

            if (this.isFinishedStatus(status)) return this.getLegendColor("total");
            if (this.isRunningStatus(status)) return this.getLegendColor("running");
            if (status === "Iniciado" || status === "Started" || status === "Setup") {
                return this.getLegendColor("started");
            }

            return this.getLegendColor("notStarted");
        },
        resolveOrderRowColor(order) {
            const explicitColor = this.getOrderField(order, "statusColor");

            if (explicitColor) return explicitColor;

            const operations = this.normalizeOperations(order);

            if (!operations.length) return null;

            const statuses = operations.map((operation) => this.getOperationStatus(operation));

            if (statuses.some((status) => this.isRunningStatus(status))) {
                return this.getLegendColor("running");
            }

            if (statuses.every((status) => this.isFinishedStatus(status))) {
                return this.getLegendColor("total");
            }

            if (
                statuses.some((status) => this.isFinishedStatus(status)) &&
                statuses.every(
                    (status) => this.isFinishedStatus(status) || this.isNotStartedStatus(status)
                )
            ) {
                return this.getLegendColor("partial");
            }

            if (statuses.some((status) => status === "Iniciado" || status === "Started" || status === "Setup")) {
                return this.getLegendColor("started");
            }

            if (statuses.every((status) => this.isNotStartedStatus(status))) {
                return this.getLegendColor("notStarted");
            }

            return null;
        },
        getOrderProgress(order) {
            const value = this.getOrderField(order, "completedPercentage");

            if (value !== "") {
                const percentage = Number(value);

                if (!Number.isNaN(percentage)) {
                    return Math.min(100, Math.max(0, percentage));
                }
            }

            const operations = this.normalizeOperations(order);

            if (!operations.length) return 0;

            const total = operations.reduce((sum, operation) => {
                const operationPercentage = Number(
                    this.getOperationField(operation, "fulfillmentPercentage")
                );

                return sum + (Number.isNaN(operationPercentage) ? 0 : operationPercentage);
            }, 0);

            return Math.min(100, Math.max(0, total / operations.length));
        },
        getProgressBarColor(percentage) {
            if (percentage >= 100) return "#4CAF50";
            if (percentage < 30) return "#D64545";

            return "#F5A623";
        },
        formatProgressLabel(percentage) {
            const roundedPercentage = Math.round(percentage * 100) / 100;

            if (Number.isInteger(roundedPercentage)) {
                return `${roundedPercentage}%`;
            }

            return `${roundedPercentage.toLocaleString(
                this.$cookies.get("userInfo")?.language || "pt-BR",
                {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                }
            )}%`;
        },
        getContrastTextColor(backgroundColor) {
            const hex = String(backgroundColor).replace("#", "");

            if (hex.length !== 6) return "#4c4541";

            const red = parseInt(hex.substring(0, 2), 16);
            const green = parseInt(hex.substring(2, 4), 16);
            const blue = parseInt(hex.substring(4, 6), 16);
            const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

            return luminance < 0.55 ? "#ffffff" : "#4c4541";
        },
        formatDate(value) {
            if (!value) return "";

            const language = this.$cookies.get("userInfo")?.language;

            if (language === "pt-BR") {
                return moment(value).format("DD/MM/YYYY");
            }

            return moment(value).format("MM/DD/YYYY");
        },
        formatQuantity(value) {
            if (value === "" || value === null || value === undefined) return "";

            const numberValue = Number(value);

            if (Number.isNaN(numberValue)) return value;

            return numberValue.toLocaleString(this.$cookies.get("userInfo")?.language || "pt-BR");
        },
        formatPercentage(value) {
            if (value === "" || value === null || value === undefined) return "";

            const numberValue = Number(value);

            if (Number.isNaN(numberValue)) return value;

            return `${numberValue}%`;
        }
    }
};
</script>

<style lang="scss">
.app-content-operator:has(#order-tracking) {
    padding: 0 !important;
}

#order-tracking {
    width: 100%;

    .header-operator {
        display: flex;
        align-items: center;
        gap: 16px;
        width: 100%;
        padding: 16px 29px;
        background: #ffffff;
        color: #4c4541;
        font-family: "Raleway", sans-serif;
        font-weight: 600;
        font-size: 22px;
        line-height: 30px;
        border-top: 1px solid #cfc4be;
        box-shadow: 0px 8px 30px rgba(58, 46, 38, 0.1);

        .header__back {
            display: flex;
            cursor: pointer;
        }
    }

    .order-tracking__content {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 24px 29px 109px;
    }
}

@media screen and (min-width: 576px) and (max-width: 768px) {
    #order-tracking {
        padding-top: 49px;

        .header-operator {
            font-size: 24px;
            line-height: 32px;
        }
    }
}

@media screen and (max-width: 575px) {
    #order-tracking {
        .header-operator {
            padding: 16px !important;
        }

        .order-tracking__content {
            padding: 24px 16px 109px;
        }
    }
}
</style>

<style lang="scss" scoped>
.order-tracking {

    &__filters-card,
    &__legend-card,
    &__table-card {
        background: #ffffff;
        border: 1px solid #cfc4be;
        border-radius: 6px;
        padding: 16px;
    }

    &__filters-row {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 16px 24px;
    }

    &__filter-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1 1 320px;
        min-width: 0;
    }

    &__filter-label {
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #998f8a;
    }

    &__filter-buttons {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        border: 1px solid #cfc4be;
        border-radius: 6px;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    &__filter-btn {
        flex: 1 0 auto;
        min-width: 88px;
        padding: 10px 12px;
        border: none;
        background: #fafafa;
        color: #998f8a;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        white-space: nowrap;

        &:not(:last-child) {
            border-right: 1px solid #cfc4be;
        }

        &.active {
            background: #974900;
            color: #ffffff;
        }
    }

    &__input-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 0 1 220px;
        min-width: 160px;
    }

    &__date-group {
        flex: 0 1 180px;
    }

    &__clear-filter-btn {
        height: 40px;
        padding: 8px 16px;
        border: 1px solid #974900;
        border-radius: 6px;
        background: #ffffff;
        color: #974900;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        white-space: nowrap;
        cursor: pointer;

        &:hover:not(:disabled) {
            background: #faf5f0;
        }

        &:disabled {
            border-color: #cfc4be;
            color: #998f8a;
            background: #fafafa;
            cursor: not-allowed;
        }
    }

    &__input-label {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        color: #4c4541;
    }

    &__input {
        width: 100%;
        height: 40px;
        padding: 8px 12px;
        border: 1px solid #cfc4be;
        border-radius: 6px;
        font-size: 14px;
        line-height: 20px;
        color: #4c4541;
        background: #ffffff;

        &:focus {
            outline: none;
            border-color: #974900;
        }
    }

    &__legend-card {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px 24px;
    }

    &__legend-title {
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        color: #4c4541;
    }

    &__legend-items {
        display: flex;
        flex-wrap: wrap;
        gap: 12px 20px;
    }

    &__legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__legend-color {
        width: 20px;
        height: 20px;
        border-radius: 4px;
        border: 1px solid rgba(76, 69, 65, 0.15);
        flex-shrink: 0;
    }

    &__legend-text {
        font-size: 14px;
        line-height: 20px;
        color: #4c4541;
    }

    &__section-title {
        margin: 0 0 12px;
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
        color: #4c4541;
    }

    &__table-wrapper {
        width: 100%;
        overflow-x: auto;
    }

    &__pagination {
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
    }

    &__table {
        width: 100%;
        min-width: 760px;
        border-collapse: collapse;

        th,
        td {
            padding: 10px 12px;
            border-bottom: 1px solid #ece0db;
            font-size: 14px;
            line-height: 20px;
            color: inherit;
            text-align: left;
            vertical-align: middle;
        }

        th {
            background: #fafafa;
            color: #4c4541;
            font-weight: 600;
            white-space: nowrap;
        }

        tbody tr {
            cursor: pointer;
        }
    }

    &__operations-card tbody tr {
        cursor: default;
    }

    &__row--selected {
        outline: 2px solid #974900;
        outline-offset: -2px;
    }

    &__empty-cell {
        text-align: center !important;
        color: #998f8a !important;
        padding: 32px 12px !important;
        cursor: default !important;
    }

    &__progress-cell {
        min-width: 140px;
    }

    &__progress {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__progress-track {
        flex: 1;
        min-width: 72px;
        height: 10px;
        background: #ece0db;
        border-radius: 999px;
        overflow: hidden;
    }

    &__progress-fill {
        height: 100%;
        border-radius: 999px;
        transition: width 0.2s ease;
    }

    &__progress-label {
        min-width: 44px;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        text-align: right;
        white-space: nowrap;
    }
}

@media screen and (max-width: 575px) {
    .order-tracking {
        &__filters-row {
            flex-direction: column;
            align-items: stretch;
        }

        &__filter-group,
        &__input-group,
        &__date-group {
            flex: 1 1 auto;
            width: 100%;
            min-width: 0;
        }

        &__filter-btn {
            min-width: 72px;
            font-size: 12px;
            padding: 8px 10px;
        }

        &__legend-card {
            flex-direction: column;
            align-items: flex-start;
        }
    }
}
</style>
